// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * ToggleControl - Boolean toggle switch
 *
 * Shows label and description from schema.
 */

interface ToggleControlProps {
  label: string;
  value: boolean;
  description?: string;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}

export function ToggleControl({
  label,
  value,
  description,
  onChange,
  disabled = false,
}: ToggleControlProps) {
  const handleToggle = () => {
    if (!disabled) {
      onChange(!value);
    }
  };

  return (
    <div
      className="mb-4"
      data-testid={`toggle-control-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between">
        {/* Label and description */}
        <div className="flex-1">
          <label className="text-sm font-medium block">{label}</label>
          {description && <p className="text-xs text-gray-400 mt-1">{description}</p>}
        </div>

        {/* Toggle switch */}
        <button
          type="button"
          role="switch"
          aria-checked={value}
          onClick={handleToggle}
          disabled={disabled}
          className={`
            relative inline-flex h-6 w-11 items-center rounded-full transition-colors
            focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900
            ${value ? 'bg-purple-600' : 'bg-gray-600'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
          data-testid="toggle-switch"
        >
          <span
            className={`
              inline-block h-4 w-4 transform rounded-full bg-white transition-transform
              ${value ? 'translate-x-6' : 'translate-x-1'}
            `}
          />
        </button>
      </div>
    </div>
  );
}
